import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config/env';

// Type for error response from API
interface ApiErrorResponse {
  statusCode?: number;
  message?: string;
  error?: string;
}

export class HttpClient {
  private axiosInstance: AxiosInstance;
  private getAccessToken: () => string | null;
  private getRefreshToken: () => string | null;
  private onTokenRefresh: (accessToken: string, refreshToken: string) => void;
  private onLogout: () => void;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(
    getAccessToken: () => string | null,
    getRefreshToken: () => string | null,
    onTokenRefresh: (accessToken: string, refreshToken: string) => void,
    onLogout: () => void,
  ) {
    this.getAccessToken = getAccessToken;
    this.getRefreshToken = getRefreshToken;
    this.onTokenRefresh = onTokenRefresh;
    this.onLogout = onLogout;

    this.axiosInstance = axios.create({
      baseURL: config.apiBase,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) =>
        Promise.reject(error instanceof Error ? error : new Error(String(error))),
    );

    // Response interceptor to handle 401 errors and token refresh
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiErrorResponse>) => {
        const originalRequest = error.config;

        // Check if error is 401 and we haven't tried to refresh yet
        if (
          error.response?.status === 401
          && originalRequest
          && !(originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry
        ) {
          (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

          // Try to refresh token
          const refreshed = await this.refreshToken();

          if (refreshed) {
            // Retry original request with new token
            const token = this.getAccessToken();
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return this.axiosInstance(originalRequest);
          }
          else {
            // Refresh failed, logout user
            this.onLogout();
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async refreshToken(): Promise<boolean> {
    // If refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.onLogout();
      return false;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await axios.post<{
          accessToken: string;
          refreshToken: string;
          tokenType: string;
          expiresIn: number;
        }>(
          `${config.apiBase}/v1/auth/refresh`,
          { refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
          },
        );

        if (response.data.accessToken) {
          this.onTokenRefresh(response.data.accessToken, response.data.refreshToken);
          return true;
        }

        this.onLogout();
        return false;
      }
      catch (error) {
        console.error('Token refresh failed:', error);
        this.onLogout();
        return false;
      }
      finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async get<T>(
    url: string,
    params?: Record<string, unknown>,
    config?: { headers?: Record<string, string> },
  ): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, {
      params,
      headers: config?.headers,
    });
    // Handle 204 No Content - axios returns empty data for 204
    if (response.status === 204) {
      const err = new Error('No content');
      (err as Error & { response: { status: number } }).response = { status: 204 };
      throw err;
    }
    return response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string> },
  ): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, {
      headers: config?.headers,
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url);
    return response.data;
  }
}
